/**
 * Newest Connector version this machine is offered + its installer link.
 * Staged rollout: a canary machine is offered a newer tag than the fleet. Takes the newest
 * of the hash-aware tag fetch and the Connector's own maestro://update-available event
 * (Rust check, also hash-aware), and resolves the download for exactly that version.
 */
import { useEffect, useState } from 'react';
import { fetchConnectorLatestPublishedVersion } from '../utils/connectorLatestVersion';
import { resolveOfferedDownloadUrl } from '../utils/connectorOfferedDownload';
import { connectorMeetsMinVersion, parseConnectorVersion } from '../utils/connectorVersion';

function bare(v) {
  return String(v || '').replace(/^v/i, '').trim();
}

export function useConnectorUpdateOffer(enabled) {
  const [offer, setOffer] = useState({ version: null, downloadUrl: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let best = null;
    const unlisteners = [];

    const consider = async (raw, fallbackUrl) => {
      const version = bare(raw);
      if (!version || !parseConnectorVersion(version)) return;
      if (best && connectorMeetsMinVersion(best, version)) return;
      best = version;
      const downloadUrl = await resolveOfferedDownloadUrl(version, fallbackUrl || null);
      if (!cancelled && best === version) setOffer({ version, downloadUrl });
    };

    (async () => {
      try {
        const ev = typeof window !== 'undefined' ? window.__TAURI__?.event : null;
        if (typeof ev?.listen === 'function') {
          const un = await ev.listen('maestro://update-available', (e) => {
            const p = e?.payload;
            if (typeof p === 'string') { void consider(p, null); return; }
            void consider(p?.version || p?.remote_version, p?.downloadUrl || p?.download_url);
          });
          if (typeof un === 'function') { if (cancelled) un(); else unlisteners.push(un); }
        }
      } catch { /* event API optional */ }
      try {
        const version = await fetchConnectorLatestPublishedVersion();
        if (!cancelled && version) await consider(version, null);
      } catch { /* ignore */ }
    })();

    return () => {
      cancelled = true;
      unlisteners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    };
  }, [enabled]);

  return offer;
}
